import React, { useState } from "react";
import {
  Button,
  Header,
  Modal,
  Form,
  TextArea,
  Input,
  Grid,
  Segment,
  Checkbox,
  Message,
} from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "../../../../app/common/modals/modalSlice";
import {
  addEmailToDb,
  sendComplianceNotificationEmail,
} from "../../../../app/firestore/firestoreService";
import { convertAddressFull } from "../../../../app/common/util/util";
import { serverTimestamp } from "firebase/firestore";
import { toast } from "react-toastify";
import ModalWrapper from "../../../../app/common/modals/modalWrapper";
import { useMediaQuery } from "react-responsive";

export default function DocFillNotifyTeamModal({ doc, transaction }) {
  const dispatch = useDispatch();
  const { currentUserProfile } = useSelector((state) => state.profile);
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });

  const transactionAddress = transaction?.address
    ? convertAddressFull(transaction.address)
    : transaction?.title || "";

  const [selectedRecipients, setSelectedRecipients] = useState({
    agent: false,
    coAgent: false,
    tc: false,
    assistant: false,
    manager: false,
    managerAssistant: false,
  });

  const [customSubject, setCustomSubject] = useState(
    `Document Update: ${doc?.name || "Document"} – ${transactionAddress}`
  );

  const [customMessage, setCustomMessage] = useState(
    `A document has been updated for the transaction at ${transactionAddress}.\n\nDocument: ${doc?.name || ""}\n\nPlease review the document at your earliest convenience.`
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const availableRecipients = {
    agent: {
      label: "Agent",
      email: transaction?.agentProfile?.email,
      name: `${transaction?.agentProfile?.firstName || ""} ${transaction?.agentProfile?.lastName || ""}`.trim(),
    },
    coAgent: {
      label: "Co-Agent",
      email: transaction?.coAgent?.email,
      name: `${transaction?.coAgent?.firstName || ""} ${transaction?.coAgent?.lastName || ""}`.trim(),
      available: !!transaction?.coAgentId,
    },
    tc: {
      label: "TC (Mine)",
      email: transaction?.transactionCoordinator?.email,
      name: `${transaction?.transactionCoordinator?.firstName || ""} ${transaction?.transactionCoordinator?.lastName || ""}`.trim(),
      available: !!transaction?.tcId,
    },
    assistant: {
      label: "Assistant",
      email: transaction?.assistant?.email,
      name: `${transaction?.assistant?.firstName || ""} ${transaction?.assistant?.lastName || ""}`.trim(),
      available: !!transaction?.hasAssistant,
    },
    manager: {
      label: "Manager",
      email: transaction?.managerDetails?.managerEmail,
      name: `${transaction?.managerDetails?.firstName || ""} ${transaction?.managerDetails?.lastName || ""}`.trim(),
      available: !!transaction?.managerId,
    },
    managerAssistant: {
      label: "Manager Assistant",
      email: transaction?.managerAssistantProfile?.email,
      name: `${transaction?.managerAssistantProfile?.firstName || ""} ${transaction?.managerAssistantProfile?.lastName || ""}`.trim(),
      available: !!transaction?.managerAssistantId,
    },
  };

  function handleRecipientToggle(recipientKey) {
    setSelectedRecipients((prev) => ({
      ...prev,
      [recipientKey]: !prev[recipientKey],
    }));
  }

  async function handleSend() {
    const hasRecipient = Object.values(selectedRecipients).some((v) => v);
    if (!hasRecipient) {
      toast.error("Please select at least one recipient");
      return;
    }

    setIsSubmitting(true);

    try {
      const toEmails = [];
      const toEmailsAndRoles = [];
      Object.entries(selectedRecipients).forEach(([key, isSelected]) => {
        if (isSelected && availableRecipients[key]?.email) {
          const recipient = availableRecipients[key];
          toEmails.push(recipient.email);
          toEmailsAndRoles.push({
            role: recipient.label,
            email: recipient.email,
            firstName: recipient.name?.split(" ")[0] || "",
            lastName: recipient.name?.split(" ").slice(1).join(" ") || "",
          });
        }
      });

      const emailHistoryData = {
        transactionId: transaction?.id,
        transactionTitle: transaction?.title,
        docId: doc?.id,
        docName: doc?.name,
        from: currentUserProfile.email,
        fromName: `${currentUserProfile.firstName} ${currentUserProfile.lastName}`,
        to: toEmails,
        toEmails,
        toEmailsAndRoles,
        subject: customSubject,
        message: customMessage,
        type: "doc-team-notification",
        sentAt: serverTimestamp(),
      };

      const mailOptions = {
        to: toEmails,
        from: `${currentUserProfile.firstName} ${currentUserProfile.lastName} <help@transactioner.com>`,
        replyTo: currentUserProfile.email,
        message: {
          subject: customSubject,
          text: customMessage,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
              <p style="white-space: pre-line;">${customMessage.replace(/\n/g, "<br/>")}</p>
              <hr/>
              <p style="color: #666; font-size: 12px;">
                Sent from Transactioner by ${currentUserProfile.firstName} ${currentUserProfile.lastName}
              </p>
            </div>
          `,
        },
      };

      await sendComplianceNotificationEmail(mailOptions);
      await addEmailToDb(emailHistoryData, { message: mailOptions.message });

      toast.success("Team notification sent successfully!");
      dispatch(closeModal({ modalType: "DocFillNotifyTeamModal" }));
    } catch (error) {
      console.error("Error sending team notification:", error);
      toast.error("Failed to send notification. Please try again.");
      setIsSubmitting(false);
    }
  }

  function handleCancel() {
    dispatch(closeModal({ modalType: "DocFillNotifyTeamModal" }));
  }

  return (
    <ModalWrapper size="large">
      <Segment clearing>
        <div
          className={
            isMobile
              ? "zero horizontal margin small top margin"
              : "medium horizontal margin small top margin"
          }
        >
          <Header icon="mail" content="Notify Team" />
          <Modal.Content>
            <Message info>
              <Message.Header>Notify Team Members</Message.Header>
              <p>
                Send a notification about this document to selected team members.
              </p>
            </Message>

            <Form>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <label>
                      <strong>Select Recipients:</strong>
                    </label>
                    <div className="small top margin">
                      {Object.entries(availableRecipients).map(([key, recipient]) => {
                        if (recipient.available === false) return null;
                        if (!recipient.email) return null;
                        return (
                          <div key={key} className="tiny bottom margin">
                            <Checkbox
                              label={`${recipient.label} – ${recipient.name} (${recipient.email})`}
                              checked={selectedRecipients[key]}
                              onChange={() => handleRecipientToggle(key)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <label>
                      <strong>Subject Line:</strong>
                    </label>
                    <Input
                      fluid
                      value={customSubject}
                      onChange={(e) => setCustomSubject(e.target.value)}
                      placeholder="Email subject"
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <label>
                      <strong>Message:</strong>
                    </label>
                    <TextArea
                      rows={5}
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      placeholder="Enter your message here..."
                      style={{ width: "100%" }}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <div className="float-right">
                      <Button onClick={handleCancel} disabled={isSubmitting}>
                        Cancel
                      </Button>
                      <Button
                        primary
                        onClick={handleSend}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        Send
                      </Button>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          </Modal.Content>
        </div>
      </Segment>
    </ModalWrapper>
  );
}

